import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
  OnInit,
} from '@angular/core';
import { NgClass } from '@angular/common';
import { Router } from '@angular/router';
import { APP_PATHS } from '../../../core/config/app-paths';
import { UserApi } from '../../../shared/services/api/user.api';
import { PrimeIcons } from 'primeng/api';
import { MenuItemList } from '../../../shared/models/custom-types';
import { SpinnerService } from '../../../core/services/spinner.service';
import { InfoSection } from '../info-section/info-section';
import { environment } from '../../../environments/environment';
import { AuthApi } from '../../../shared/services/api/auth.api';
import { catchError, EMPTY, tap } from 'rxjs';
import {
  HttpErrorDetails,
  HttpResponseService,
} from '../../../core/services/http-error-response.service';
import { HttpErrorResponse } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuthTechRequirementsAndManualComponent } from '../auth-tech-requirements-and-manual/auth-tech-requirements-and-manual';
import { ErrorCode } from '../../../shared/enums/error-code.enum';

@Component({
  selector: 'app-login-method',
  imports: [NgClass, InfoSection, AuthTechRequirementsAndManualComponent],
  templateUrl: './login-method.component.html',
  styleUrl: './login-method.component.scss',
  providers: [UserApi],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoginMethodComponent implements OnInit {
  readonly #router = inject(Router);
  readonly #spinnerService = inject(SpinnerService);
  readonly #authApi = inject(AuthApi);
  readonly #destroyRef = inject(DestroyRef);
  readonly #httpResponseService = inject(HttpResponseService);

  public errorId = input<string>();

  protected AUTH_METHODS_MENU_ITEM: MenuItemList[] = [
    {
      id: '1',
      title: 'login.gov.pl',
      label: 'Profil zaufany, bankowość, mObywatel, e-Dowód lub eID',
      icon: PrimeIcons.ID_CARD,
      action: () => this.navigateToUserContext(),
    },
    ...(environment.QUALIFIED_SIGNATRE_AUTH_VISIBLE
      ? [
          {
            id: '2',
            title: 'Kwalifikowany podpis elektroniczny',
            label: 'Certyfikat przypisany do konkretnej osoby',
            icon: PrimeIcons.SHIELD,
            action: () => this.loginByElectronicSignature(),
          },
        ]
      : []),
    ...(!environment.production
      ? [
          {
            id: '3',
            title: 'Logowanie dla deweloperów',
            label: 'Widoczny tylko na środowiskach STAGE oraz TEST',
            icon: PrimeIcons.KEY,
            action: () => this.loginForDevelopers(),
          },
        ]
      : []),
  ];

  ngOnInit(): void {
    this.#handleErrorToken();
  }

  protected navigateToUserContext(): void {
    window.location.href = environment.API_EWNIOSKI + '/auth/sso/logingovpl';
  }

  protected loginByElectronicSignature(): void {
    this.#authApi
      .getCurrentUser$()
      .pipe(
        tap(() => {
          this.#router.navigate([APP_PATHS.home]);
        }),
        catchError(() => {
          this.#spinnerService.show();
          this.#router
            .navigate([
              APP_PATHS.auth.root,
              APP_PATHS.auth.authElectronicSignature,
            ])
            .finally(() => {
              window.location.reload(); //odświeżenie strony wymuszone jest przez błędy występujące w SZAFIR_SDK_WEB, który bez odśieżenia strony niepoprawnie ładuje aplet
            });
          return EMPTY;
        }),
      )
      .subscribe();
  }

  protected loginForDevelopers(): void {
    this.#authApi
      .getCurrentUser$()
      .pipe(
        tap(() => {
          this.#router.navigate([APP_PATHS.home]);
        }),
        catchError(() => {
          this.#router.navigate([
            APP_PATHS.auth.root,
            APP_PATHS.auth.authDevelopers,
          ]);
          return EMPTY;
        }),
      )
      .subscribe();
  }

  #handleErrorToken(): void {
    if (this.errorId() != null) {
      this.#clearUrlFromErrorCode();
      this.#authApi
        .getWkError$(this.errorId()!)
        .pipe(
          tap((error: HttpErrorDetails) => {
            const defaultMessage = 'Wystąpił błąd podczas logowania.';
            const isAccountDisabledError =
              error?.errorCode === ErrorCode.ACCOUNT_DISABLED;

            const message = isAccountDisabledError
              ? error?.message || defaultMessage
              : defaultMessage;

            const errorDetails = isAccountDisabledError
              ? { ...error, token: undefined }
              : error;

            this.#httpResponseService.showErrorMessage(
              new HttpErrorResponse({ error: errorDetails }),
              message,
            );
          }),
          catchError(() => {
            return EMPTY;
          }),
          takeUntilDestroyed(this.#destroyRef),
        )
        .subscribe();
    }
  }

  #clearUrlFromErrorCode(): void {
    const url = new URL(window.location.href);
    url.searchParams.delete('errorId');
    window.history.replaceState({}, document.title, url.toString());
  }
}
